import type { FetchMatcher, JsonApiDoc, JsonApiResource } from 'tio-data/demo/types';
import {
  getDemoDebtLevel,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';
import { PERSON_ID } from 'tio-employee/demo/identity-fixtures';

/**
 * Loan-document list and preview reads bypass SkipCache interception and are matched at the fetch seam.
 * The not-linked debt scenario returns no documents; inline data: PDF assets keep previews network-free.
 */

// A minimal, valid one-page PDF ("Demo Loan Statement"), base64-inlined so the
// preview is network-free. Generated with correct xref offsets; ~690 bytes.
const DEMO_STATEMENT_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iago8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4KZW5kb2JqCjIgMCBvYmoKPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFszIDAgUl0gL0NvdW50IDEgPj4KZW5kb2JqCjMgMCBvYmoKPDwgL1R5cGUgL1BhZ2UgL1BhcmVudCAyIDAgUiAvTWVkaWFCb3ggWzAgMCAzMDYgMzk2XSAvQ29udGVudHMgNCAwIFIgL1Jlc291cmNlcyA8PCAvRm9udCA8PCAvRjEgNSAwIFIgPj4gPj4gPj4KZW5kb2JqCjQgMCBvYmoKPDwgL0xlbmd0aCAxNDYgPj4Kc3RyZWFtCkJUIC9GMSAxNiBUZiAzNiAzNDAgVGQgKERlbW8gTG9hbiBTdGF0ZW1lbnQpIFRqIDAgLTI4IFRkIC9GMSAxMSBUZiAoTU9IRUxBIFwyNjcgQWNjb3VudCAqKioqMjIxMCkgVGogMCAtMjAgVGQgKEN1cnJlbnQgYmFsYW5jZTogJDE1MCwwMDAuMDApIFRqIEVUCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PCAvVHlwZSAvRm9udCAvU3VidHlwZSAvVHlwZTEgL0Jhc2VGb250IC9IZWx2ZXRpY2EgPj4KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMDU4IDAwMDAwIG4gCjAwMDAwMDAxMTUgMDAwMDAgbiAKMDAwMDAwMDI0MSAwMDAwMCBuIAowMDAwMDAwNDM4IDAwMDAwIG4gCnRyYWlsZXIKPDwgL1NpemUgNiAvUm9vdCAxIDAgUiA+PgpzdGFydHhyZWYKNTA4CiUlRU9G';

interface DemoDocument {
  id: string;
  filename: string;
  via: string;
  createdAt: string;
  provider: string;
}

// Two realistic loan documents: an emailed NSLDS statement and an uploaded
// servicer statement. States chosen so getStatus() → 'APPLIED' (success chip):
// extraction PROCESSED → reflection APPLIED; subsistence not IN_DISPUTE.
const DEMO_DOCUMENTS: DemoDocument[] = [
  {
    id: 'demo-loan-doc-1',
    filename: 'nslds-loan-summary-2026-05.pdf',
    via: 'EMAIL',
    createdAt: '2026-05-15T09:12:00Z',
    provider: 'ObservableProvider.NSLDS',
  },
  {
    id: 'demo-loan-doc-2',
    filename: 'mohela-statement-2026-04.pdf',
    via: 'UPLOAD',
    createdAt: '2026-04-02T14:30:00Z',
    provider: 'ObservableProvider.ACCOUNT_STATEMENT',
  },
];

function assetResource(doc: DemoDocument): JsonApiResource {
  return {
    type: 'observable-asset',
    id: `${doc.id}-asset`,
    attributes: {
      filename: doc.filename,
      contentType: 'application/pdf',
      signedUrl: DEMO_STATEMENT_PDF,
    },
    relationships: {
      observableDocument: { data: { type: 'observable-document', id: doc.id } },
    },
  };
}

function documentResource(doc: DemoDocument): JsonApiResource {
  return {
    type: 'observable-document',
    id: doc.id,
    attributes: {
      via: doc.via,
      provider: doc.provider,
      createdAt: doc.createdAt,
      // getStatus(): extraction PROCESSED → reads reflectionState → 'APPLIED'
      // (success chip). subsistenceState anything but IN_DISPUTE.
      extractionState: 'ExtractionState.PROCESSED',
      reflectionState: 'ReflectionState.APPLIED',
      subsistenceState: 'SubsistenceState.IMPLICIT',
    },
    relationships: {
      observableAsset: { data: { type: 'observable-asset', id: `${doc.id}-asset` } },
      person: { data: { type: 'person', id: PERSON_ID } },
    },
  };
}

/**
 * Fetch-seam matchers for the account-activity documents table + preview.
 * Returns [] when the session has no linked loans.
 */
export function buildLoanDocumentFetchMatchers(
  selection: DemoSessionSelectionSnapshot
): FetchMatcher[] {
  const debt = getDemoDebtLevel(selection.debtLevelId);
  if (!debt.hasLinkedLoans) {
    return [];
  }

  const documents = DEMO_DOCUMENTS.map(documentResource);
  const assets = DEMO_DOCUMENTS.map(assetResource);

  const collection: JsonApiDoc = {
    data: documents,
    included: assets,
    meta: { page: { total: documents.length } },
  };

  return [
    // Row "preview": findRecord('observable-document', id) — MORE specific, so
    // it must precede the collection matcher. Serve the requested doc (ids come
    // from the collection above) with its asset sideloaded for signedUrl.
    {
      urlMatches: (url: string) => /\/observable-documents\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        // Serve ONLY the requested id (the preview always asks for an id from
        // the collection above). No fallback to DEMO_DOCUMENTS[0] — an unknown
        // id returns data: null so a wrong-id request surfaces (blank preview)
        // instead of silently showing the first document.
        const id = /\/observable-documents\/([^/?]+)/.exec(url)?.[1];
        const doc = DEMO_DOCUMENTS.find((d) => d.id === id);
        if (!doc) {
          return { data: null };
        }
        return { data: documentResource(doc), included: [assetResource(doc)] };
      },
    },
    // Documents table: query('observable-document', { filter[person], … }).
    {
      urlMatches: (url: string) => /\/observable-documents(\?|$)/.test(url),
      method: 'GET',
      json: collection,
    },
  ];
}
